'use client';

/**
 * Cross-Filter Context
 * 
 * Provides cross-filtering between visualizations.
 * When user clicks/selects on one chart, all other charts
 * in the same context automatically filter.
 */

import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { applyFilters, registerDataset, getDataset, type CrossFilter } from './data-engine';

// ============= TYPES =============

export interface FilterSelection {
  chartId: string;
  field: string;
  values: any[];
  operator: 'in' | 'range' | 'eq';
}

export interface ChartRegistration {
  id: string;
  datasetId: string;
  fields: string[];  // Fields this chart can filter on
}

interface CrossFilterState {
  datasetId: string | null;
  originalData: any[];
  filteredData: any[];
  filters: Map<string, FilterSelection>;  // chartId -> selection
  charts: Map<string, ChartRegistration>;
  isFiltering: boolean;
}

type CrossFilterAction =
  | { type: 'SET_DATASET'; datasetId: string; data: any[] }
  | { type: 'ADD_FILTER'; selection: FilterSelection }
  | { type: 'REMOVE_FILTER'; chartId: string }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REGISTER_CHART'; chart: ChartRegistration }
  | { type: 'UNREGISTER_CHART'; chartId: string }
  | { type: 'SET_FILTERED_DATA'; data: any[] }
  | { type: 'SET_FILTERING'; isFiltering: boolean };

interface CrossFilterContextValue {
  state: CrossFilterState;
  setDataset: (datasetId: string, data: any[]) => Promise<void>;
  addFilter: (selection: FilterSelection) => void;
  removeFilter: (chartId: string) => void;
  clearFilters: () => void;
  registerChart: (chart: ChartRegistration) => void;
  unregisterChart: (chartId: string) => void;
  getFilteredData: () => any[];
  getActiveFilters: () => FilterSelection[];
  isChartFiltered: (chartId: string) => boolean;
}

// ============= REDUCER =============

const initialState: CrossFilterState = {
  datasetId: null,
  originalData: [],
  filteredData: [],
  filters: new Map(),
  charts: new Map(),
  isFiltering: false,
};

function reducer(state: CrossFilterState, action: CrossFilterAction): CrossFilterState {
  switch (action.type) {
    case 'SET_DATASET':
      return {
        ...state,
        datasetId: action.datasetId,
        originalData: action.data,
        filteredData: action.data,
        filters: new Map(),
      };
    
    case 'ADD_FILTER': {
      const newFilters = new Map(state.filters);
      newFilters.set(action.selection.chartId, action.selection);
      return { ...state, filters: newFilters };
    }
    
    case 'REMOVE_FILTER': {
      const newFilters = new Map(state.filters);
      newFilters.delete(action.chartId);
      return { ...state, filters: newFilters };
    }
    
    case 'CLEAR_FILTERS':
      return { 
        ...state, 
        filters: new Map(), 
        filteredData: state.originalData,
      };
    
    case 'REGISTER_CHART': {
      const newCharts = new Map(state.charts);
      newCharts.set(action.chart.id, action.chart);
      return { ...state, charts: newCharts };
    }
    
    case 'UNREGISTER_CHART': {
      const newCharts = new Map(state.charts);
      const newFilters = new Map(state.filters);
      newCharts.delete(action.chartId);
      newFilters.delete(action.chartId);
      return { ...state, charts: newCharts, filters: newFilters };
    }
    
    case 'SET_FILTERED_DATA':
      return { ...state, filteredData: action.data, isFiltering: false };
    
    case 'SET_FILTERING':
      return { ...state, isFiltering: action.isFiltering };
    
    default:
      return state;
  }
}

// ============= CONTEXT =============

const CrossFilterContext = createContext<CrossFilterContextValue | null>(null);

export function CrossFilterProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Apply filters when they change
  useEffect(() => {
    if (!state.datasetId || state.filters.size === 0) {
      if (state.filteredData !== state.originalData) {
        dispatch({ type: 'SET_FILTERED_DATA', data: state.originalData });
      }
      return;
    }
    
    // Debounce filter application
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    
    dispatch({ type: 'SET_FILTERING', isFiltering: true });
    
    filterDebounceRef.current = setTimeout(async () => {
      try {
        const filters: CrossFilter[] = Array.from(state.filters.values()).map(f => ({
          field: f.field,
          values: f.values,
          operator: f.operator,
        }));
        
        const filtered = await applyFilters(state.datasetId!, filters);
        dispatch({ type: 'SET_FILTERED_DATA', data: filtered });
      } catch (err) {
        console.error('Failed to apply filters:', err);
        dispatch({ type: 'SET_FILTERING', isFiltering: false });
      }
    }, 100);
    
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, [state.datasetId, state.filters, state.originalData]);
  
  const setDataset = useCallback(async (datasetId: string, data: any[]) => {
    // Check if already cached
    const existing = getDataset(datasetId);
    if (!existing) {
      await registerDataset(datasetId, datasetId, data);
    }
    dispatch({ type: 'SET_DATASET', datasetId, data });
  }, []);
  
  const addFilter = useCallback((selection: FilterSelection) => {
    dispatch({ type: 'ADD_FILTER', selection });
  }, []);
  
  const removeFilter = useCallback((chartId: string) => {
    dispatch({ type: 'REMOVE_FILTER', chartId });
  }, []);
  
  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);
  
  const registerChart = useCallback((chart: ChartRegistration) => {
    dispatch({ type: 'REGISTER_CHART', chart });
  }, []);
  
  const unregisterChart = useCallback((chartId: string) => {
    dispatch({ type: 'UNREGISTER_CHART', chartId });
  }, []);
  
  const getFilteredData = useCallback(() => state.filteredData, [state.filteredData]);
  
  const getActiveFilters = useCallback(() => Array.from(state.filters.values()), [state.filters]);
  
  const isChartFiltered = useCallback((chartId: string) => {
    // A chart is "filtered" if there are filters from OTHER charts
    for (const [id] of state.filters) {
      if (id !== chartId) return true;
    }
    return false;
  }, [state.filters]);
  
  const value = useMemo<CrossFilterContextValue>(() => ({
    state,
    setDataset,
    addFilter,
    removeFilter,
    clearFilters,
    registerChart,
    unregisterChart,
    getFilteredData,
    getActiveFilters,
    isChartFiltered,
  }), [state, setDataset, addFilter, removeFilter, clearFilters, registerChart, unregisterChart, getFilteredData, getActiveFilters, isChartFiltered]);
  
  return (
    <CrossFilterContext.Provider value={value}>
      {children}
    </CrossFilterContext.Provider>
  );
}

// ============= HOOKS =============

export function useCrossFilter() {
  const context = useContext(CrossFilterContext);
  if (!context) {
    throw new Error('useCrossFilter must be used within a CrossFilterProvider');
  }
  return context;
}

// Hook for individual chart
export function useChartFilter(chartId: string, fields: string[]) {
  const { 
    state, 
    addFilter, 
    removeFilter, 
    registerChart, 
    unregisterChart,
    isChartFiltered,
  } = useCrossFilter();
  
  // Register on mount
  useEffect(() => {
    if (state.datasetId) {
      registerChart({ id: chartId, datasetId: state.datasetId, fields });
    }
    return () => unregisterChart(chartId);
  }, [chartId, fields, state.datasetId, registerChart, unregisterChart]);
  
  const setFilter = useCallback((field: string, values: any[], operator: 'in' | 'range' | 'eq' = 'in') => {
    if (values.length === 0) {
      removeFilter(chartId);
    } else {
      addFilter({ chartId, field, values, operator });
    }
  }, [chartId, addFilter, removeFilter]);
  
  const clearFilter = useCallback(() => {
    removeFilter(chartId);
  }, [chartId, removeFilter]);
  
  const myFilter = state.filters.get(chartId);
  const isFiltered = isChartFiltered(chartId);
  const data = isFiltered ? state.filteredData : state.originalData;
  
  return {
    data,
    myFilter,
    isFiltered,
    setFilter,
    clearFilter,
  };
}

// ============= FILTER DISPLAY COMPONENT =============

export function ActiveFilters({ className = '' }: { className?: string }) {
  const { state, removeFilter, clearFilters } = useCrossFilter();
  
  if (state.filters.size === 0) return null;
  
  const filters = Array.from(state.filters.values());
  
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-xs text-zinc-500">Filters:</span>
      {filters.map(f => (
        <button
          key={f.chartId}
          onClick={() => removeFilter(f.chartId)}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-full hover:bg-blue-500/30 transition"
        >
          <span>{f.field}: {f.values.slice(0, 2).join(', ')}{f.values.length > 2 ? '...' : ''}</span>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ))}
      <button
        onClick={clearFilters}
        className="text-xs text-zinc-400 hover:text-white transition"
      >
        Clear all
      </button>
    </div>
  );
}
